"use client";
import React, { useState, useCallback, useEffect, lazy, Suspense } from "react";
import { Heart, Star } from "lucide-react";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { toast } from "sonner";
import { useRouter } from "next/navigation";
import BuyNow from "../../../components/Functionalities/BuyNow";
import AddToCart from "../../../components/Functionalities/AddToCart";
import TryYourself from "../../../components/Functionalities/tryYourself";
import {
  SeeMoreText,
  DetailIcons,
} from "../../../components/utility/detailsPage";

// Lazy loaded components
const Script = lazy(() => import("next/script"));
const ProductRecommendation = lazy(() =>
  import("../../../components/ProductRecommendation")
);

import ImageGallery from "../../../components/Functionalities/ImageGallery";
import ImageUploader from "../../../components/Functionalities/ImageUploader";

// Optimized ProductDetailClient component
const ProductDetailClient = ({ initialProductData }) => {
  // State initialization with null checks and default values
  const [isLiked, setIsLiked] = useState(() => {
    if (typeof window !== "undefined") {
      const likedItems = JSON.parse(localStorage.getItem("likedItems") || "[]");
      return likedItems.some((item) => item.id === initialProductData?.id);
    }
    return false;
  });

  const [currentImage, setCurrentImage] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isDesktop, setIsDesktop] = useState(false);
  const [imageFile, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [customizations, setCustomizations] = useState({
    size: initialProductData?.Size?.[0] || "",
    color: initialProductData?.Color?.[0] || "",
    message: "",
    spotifyLink: "",
  });
  const [showTryPopup, setShowTryPopup] = useState(false);

  const [currentPrice, setCurrentPrice] = useState(
    initialProductData?.productPrice || 0
  );

  const [currentOriginalPrice, setCurrentOriginalPrice] = useState(
    initialProductData?.OriginalproductPrice || 0
  );

  const router = useRouter();
  const isOutOfStock = initialProductData?.productTag === "OutOfStock";

  // Debounced mouse move handler
  const handleMouseMove = useCallback(
    (e) => {
      if (!isDesktop || isOutOfStock) return;

      const bounds = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - bounds.left) / bounds.width) * 100;
      const y = ((e.clientY - bounds.top) / bounds.height) * 100;
      setMousePosition({ x, y });
    },
    [isDesktop, isOutOfStock]
  );

  // Check for desktop view with debounce
  useEffect(() => {
    let timeout;
    const checkIsDesktop = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setIsDesktop(window.innerWidth >= 1024);
      }, 100);
    };

    checkIsDesktop();
    window.addEventListener("resize", checkIsDesktop);
    return () => {
      window.removeEventListener("resize", checkIsDesktop);
      clearTimeout(timeout);
    };
  }, []);

  // Function to generate image previews
  const generatePreviews = useCallback((files) => {
    return Array.from(files).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
  }, []);

  // Handle image selection
  const handleImageSelect = useCallback(
    (e) => {
      const files = Array.from(e.target.files);
      const validFiles = files.filter((file) => {
        const validTypes = ["image/jpeg", "image/png", "image/gif"];
        const maxSize = 5 * 1024 * 1024; // 5MB

        if (!validTypes.includes(file.type)) {
          toast.error(
            `Invalid file type for ${file.name}. Please upload JPEG, PNG, or GIF.`
          );
          return false;
        }
        if (file.size > maxSize) {
          toast.error(
            `File ${file.name} is too large. Please upload files smaller than 5MB.`
          );
          return false;
        }
        return true;
      });

      setImageFiles((prev) => [...prev, ...validFiles]);
      const newPreviews = generatePreviews(validFiles);
      setImagePreviews((prev) => [...prev, ...newPreviews]);
    },
    [generatePreviews]
  );

  // Remove image preview
  const removeImage = useCallback(
    (index) => {
      URL.revokeObjectURL(imagePreviews[index].preview);
      setImageFiles((prev) => prev.filter((_, i) => i !== index));
      setImagePreviews((prev) => prev.filter((_, i) => i !== index));
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[index];
        return newProgress;
      });
    },
    [imagePreviews]
  );

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      imagePreviews.forEach((preview) => {
        URL.revokeObjectURL(preview.preview);
      });
    };
  }, [imagePreviews]);

  // Update prices based on size selection
  const updatePrices = useCallback((size, basePrice, baseOriginalPrice) => {
    let additionalCost = 0;
    if (size === "8X10" || size === "9X7") {
      additionalCost = 100;
    } else if (size === "7.5") {
      additionalCost = 200;
    } else if (size === "9.5") {
      additionalCost = 300;
    }
    setCurrentPrice(Number(basePrice) + additionalCost);
    if (baseOriginalPrice) {
      setCurrentOriginalPrice(Number(baseOriginalPrice) + additionalCost);
    }
  }, []);

  // Optimized image upload with Promise.all for parallel uploads
  const handleImageUpload = useCallback(async (files) => {
    if (!files.length) return [];

    const uploadPromises = files.map((file, index) => {
      return new Promise((resolve, reject) => {
        const storage = getStorage();
        const storageRef = ref(storage, `uploads/${Date.now()}_${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress((prev) => ({
              ...prev,
              [index]: Math.round(progress),
            }));
          },
          reject,
          () => getDownloadURL(uploadTask.snapshot.ref).then(resolve)
        );
      });
    });

    try {
      return await Promise.all(uploadPromises);
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload some images. Please try again.");
      return [];
    }
  }, []);

  // Prepare product data for cart/purchase
  const prepareProductData = useCallback(async () => {
    if (!initialProductData) {
      toast.error("Product information is not available");
      return null;
    }

    let imageUrls = [];
    if (imageFile.length) {
      try {
        imageUrls = await handleImageUpload(imageFile);
      } catch (error) {
        toast.error("Failed to upload images. Please try again.");
        return null;
      }
    }

    return {
      id: initialProductData.id || "unknown",
      name: initialProductData.productName || "Unknown Product",
      price: currentPrice || initialProductData.productPrice,
      productImages: initialProductData.productImages || [],
      image: initialProductData.productImages?.[0] || "",
      customImage: imageUrls, // This should be an array of URLs
      message: customizations.message || "",
      spotifyLink: customizations.spotifyLink || "",
      quantity: 1,
      timestamp: Date.now(),
    };
  }, [
    initialProductData,
    imageFile,
    handleImageUpload,
    customizations,
    currentPrice,
  ]);

  // Handle size selection
  const handleSizeSelect = useCallback(
    (size) => {
      setCustomizations((prev) => ({ ...prev, size }));
      updatePrices(
        size,
        initialProductData?.productPrice,
        initialProductData?.OriginalproductPrice
      );
    },
    [initialProductData, updatePrices]
  );

  // Memoized tracking handlers
  const trackAddToCart = useCallback(() => {
    try {
      // Meta Pixel tracking
      if (window.metaPixelTracking?.trackAddToCart) {
        window.metaPixelTracking.trackAddToCart({
          id: initialProductData.id,
          productName: initialProductData.productName,
          productPrice: `${Number(initialProductData.productPrice).toFixed(
            2
          )} INR`,
          quantity: 1,
          productCategory: initialProductData.productCategory,
        });
      }

      // Google Ads tracking
      if (window.gtag) {
        window.gtag("event", "add_to_cart", {
          send_to: "AW-11530431180",
          value: initialProductData.productPrice,
          currency: "INR",
          items: [
            {
              id: initialProductData.id,
              name: initialProductData.productName,
              quantity: 1,
              price: initialProductData.productPrice,
            },
          ],
        });
      }
    } catch (error) {
      console.error("Error tracking add to cart:", error);
    }
  }, [initialProductData]);

  const trackPurchase = useCallback(() => {
    try {
      // Meta Pixel tracking
      if (window.metaPixelTracking?.trackPurchase) {
        window.metaPixelTracking.trackPurchase({
          products: [
            {
              id: initialProductData.id,
              productName: initialProductData.productName,
              productPrice: `${Number(initialProductData.productPrice).toFixed(
                2
              )} INR`,
              quantity: 1,
            },
          ],
          totalValue: `${Number(initialProductData.productPrice).toFixed(
            2
          )} INR`,
          shipping: 89,
        });
      }

      // Google Ads tracking
      if (window.gtag) {
        window.gtag("event", "purchase", {
          send_to: "AW-11530431180",
          value: initialProductData.productPrice,
          currency: "INR",
          transaction_id: Date.now().toString(),
          items: [
            {
              id: initialProductData.id,
              name: initialProductData.productName,
              quantity: 1,
              price: initialProductData.productPrice,
            },
          ],
        });
      }
    } catch (error) {
      console.error("Error tracking purchase:", error);
    }
  }, [initialProductData]);

  // Toggle like handler
  const toggleLike = useCallback(() => {
    try {
      if (!initialProductData) {
        toast.error("Product information is not available");
        return;
      }

      const likedItems = JSON.parse(localStorage.getItem("likedItems") || "[]");
      if (isLiked) {
        const updatedItems = likedItems.filter(
          (item) => item.id !== initialProductData.id
        );
        localStorage.setItem("likedItems", JSON.stringify(updatedItems));
        setIsLiked(false);
        toast.success("Removed from liked items");
      } else {
        const productToAdd = {
          id: initialProductData.id,
          name: initialProductData.productName || "Unknown Product",
          price: initialProductData.productPrice || 0,
          image: initialProductData.productImages?.[0] || "",
          timestamp: Date.now(),
        };
        likedItems.push(productToAdd);
        localStorage.setItem("likedItems", JSON.stringify(likedItems));
        setIsLiked(true);
        toast.success("Added to liked items");
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      toast.error("Failed to update liked items");
    }
  }, [initialProductData, isLiked]);

  // Defer non-critical scripts with useEffect
  useEffect(() => {
    // Initialize Google Ads tracking
    const initGoogleAnalytics = () => {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      gtag("js", new Date());
      gtag("config", "AW-11530431180");
    };

    // Load Google Analytics after page loads
    if (typeof window !== "undefined") {
      const timer = setTimeout(() => {
        initGoogleAnalytics();
      }, 1000); // Defer by 1 second

      return () => clearTimeout(timer);
    }
  }, []);

  if (!initialProductData) {
    return (
      <div className="text-center p-4 md:p-8">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">
          Product not found
        </h2>
      </div>
    );
  }

  return (
    <>
      <Suspense fallback={null}>
        <Script
          src="https://www.googletagmanager.com/gtag/js?id=AW-11530431180"
          strategy="lazyOnload"
          defer
        />
        <Script id="google-analytics" strategy="lazyOnload">
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){window.dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'AW-11530431180');
          `}
        </Script>
      </Suspense>

      <div className="bg-gradient-to-br from-red-50 to-red-100 min-h-screen p-3 md:p-6">
        <div className="max-w-7xl mx-auto bg-white rounded-2xl md:rounded-3xl shadow-lg overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Image Gallery Section */}
            <ImageGallery
              images={initialProductData.productImages}
              currentImage={currentImage}
              setCurrentImage={setCurrentImage}
              isOutOfStock={isOutOfStock}
              isDesktop={isDesktop}
              isZoomed={isZoomed}
              setIsZoomed={setIsZoomed}
              mousePosition={mousePosition}
              handleMouseMove={handleMouseMove}
            />

            {/* Product Details Section */}
            <div className="lg:w-1/2 p-4 md:p-6 lg:p-8">
              <div className="flex justify-between items-start mb-4 md:mb-6">
                <div>
                  <h1
                    className={`text-xl md:text-2xl lg:text-3xl font-bold mb-2 ${
                      isOutOfStock ? "text-gray-400" : "text-gray-900"
                    }`}
                  >
                    {initialProductData.productName}
                    {isOutOfStock && (
                      <span className="ml-2 text-sm text-red-500">
                        (Out of Stock)
                      </span>
                    )}
                  </h1>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 md:w-5 h-4 md:h-5 ${
                          isOutOfStock ? "text-gray-300" : "text-yellow-400"
                        } fill-current`}
                      />
                    ))}
                    <span className="ml-2 text-sm text-gray-600">
                      ({initialProductData.reviews || 16} reviews)
                    </span>
                  </div>
                </div>
                <button
                  onClick={toggleLike}
                  className={`p-2 rounded-full transition-colors ${
                    isLiked
                      ? "text-red-500 bg-red-50"
                      : "text-gray-400 hover:bg-gray-100"
                  }`}
                  aria-label={
                    isLiked ? "Remove from favorites" : "Add to favorites"
                  }
                >
                  <Heart
                    className="w-5 md:w-6 h-5 md:h-6"
                    fill={isLiked ? "currentColor" : "none"}
                  />
                </button>
              </div>

              <SeeMoreText
                text={initialProductData.productDescription}
                maxLength={100}
              />

              {/* Price Section */}
              <div className="flex items-baseline gap-3 mb-6 mt-8">
                <span
                  className={`text-2xl md:text-3xl font-bold ${
                    isOutOfStock ? "text-gray-400" : "text-red-600"
                  }`}
                >
                  ₹{currentPrice?.toLocaleString()}
                </span>
                {currentOriginalPrice > currentPrice && (
                  <span
                    className={`text-base md:text-lg ${
                      isOutOfStock ? "text-gray-300" : "text-gray-400"
                    } line-through`}
                  >
                    ₹{currentOriginalPrice?.toLocaleString()}
                  </span>
                )}
                <span className="text-green-600 text-sm font-medium">
                  {Math.round(
                    ((Number(currentOriginalPrice) - Number(currentPrice)) /
                      Number(currentOriginalPrice)) *
                      100
                  )}
                  % OFF
                </span>
              </div>
              <p className="text-xs font-medium text-gray-700">
                or Pay Rs.{" "}
                <span className="text-black font-bold">
                  {" "}
                  {Math.floor(currentPrice * 0.3)}{" "}
                </span>{" "}
                now and the rest on delivery using Partial COD at checkout
              </p>

              {/* Divider */}
              <div className="border-t border-gray-200 my-6"></div>

              {/* Customization Options */}
              <div className="space-y-4 md:space-y-6 mb-6 md:mb-8">
                {initialProductData.Size?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Size{" "}
                      <span className="font-normal text-gray-500">
                        (inches)
                      </span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {initialProductData.Size.map((size) => (
                        <button
                          key={size}
                          onClick={() => handleSizeSelect(size)}
                          className={`px-3 md:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                            customizations.size === size
                              ? "bg-red-500 text-white shadow-md"
                              : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {initialProductData.Color?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Color
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {initialProductData.Color.map((color) => (
                        <button
                          key={color}
                          onClick={() =>
                            setCustomizations((prev) => ({ ...prev, color }))
                          }
                          className={`px-3 md:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                            customizations.color === color
                              ? "bg-red-500 text-white shadow-md"
                              : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {color}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Message Input */}
                {initialProductData.customizationSettings
                  ?.allowCustomMessage && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Custom Message
                    </label>
                    <textarea
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm md:text-base"
                      rows="3"
                      placeholder="Add your custom message here..."
                      value={customizations.message}
                      onChange={(e) =>
                        setCustomizations((prev) => ({
                          ...prev,
                          message: e.target.value,
                        }))
                      }
                    />
                  </div>
                )}

                {/* Spotify Link Input */}
                {initialProductData.customizationSettings?.allowSpotifyLink && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Spotify Link
                    </label>
                    <input
                      type="url"
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm md:text-base"
                      placeholder="Add your Spotify link here..."
                      value={customizations.spotifyLink}
                      onChange={(e) =>
                        setCustomizations((prev) => ({
                          ...prev,
                          spotifyLink: e.target.value,
                        }))
                      }
                    />
                  </div>
                )}

                {/* Image Upload */}
                {initialProductData.customizationSettings?.allowImageUpload && (
                  <ImageUploader
                    handleImageSelect={handleImageSelect}
                    imagePreviews={imagePreviews}
                    uploadProgress={uploadProgress}
                    removeImage={removeImage}
                  />
                )}
              </div>

              <div>
                {/* Desktop view */}
                <div className="hidden sm:grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  <AddToCart
                    productId={initialProductData.id}
                    product={initialProductData}
                    prepareProductData={prepareProductData}
                    isOutOfStock={isOutOfStock}
                    customizations={customizations}
                    trackAddToCart={trackAddToCart}
                  />
                  <BuyNow
                    product={initialProductData}
                    prepareProductData={prepareProductData}
                    isOutOfStock={isOutOfStock}
                    customizations={customizations}
                    trackPurchase={trackPurchase}
                  />
                  <button
                    onClick={() => setShowTryPopup(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
                  >
                    Try
                  </button>
                </div>
              </div>

              {/* Product Details Box */}
              <div className="bg-gray-50 rounded-lg md:rounded-xl p-3 md:p-4 mb-6 md:mb-8 md:mt-3">
                <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-3">
                  Shipment Details
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm md:text-base text-gray-600">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                    This product will be shipped using our courier partners
                  </li>
                  <li className="flex items-center gap-2 text-sm md:text-base text-gray-600">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                    Products will be shipped within 5-6 days of order placement
                  </li>
                </ul>
                <DetailIcons />
              </div>

              {/* Mobile view Action Button*/}
              <div className="sm:hidden fixed bottom-0 left-0 w-full bg-white p-3 border-t z-50">
                <div className="flex gap-3 md:gap-4">
                  <div className="w-1/2">
                    <AddToCart
                      productId={initialProductData.id}
                      product={initialProductData}
                      prepareProductData={prepareProductData}
                      isOutOfStock={isOutOfStock}
                      customizations={customizations}
                      trackAddToCart={trackAddToCart}
                    />
                  </div>
                  <div className="w-1/2">
                    <BuyNow
                      product={initialProductData}
                      prepareProductData={prepareProductData}
                      isOutOfStock={isOutOfStock}
                      customizations={customizations}
                      trackPurchase={trackPurchase}
                    />
                  </div>
                  {/* <div className="w-1/3">
                    <button
                      onClick={() => setShowTryPopup(true)}
                      className="w-full bg-blue-600 text-white py-2 rounded-md text-sm"
                    >
                      Try
                    </button>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-8">
          <ProductRecommendation currentProduct={initialProductData} />
        </div>
      </div>
      {showTryPopup && (
        <TryYourself
          productId={initialProductData.id}
          onClose={() => setShowTryPopup(false)}
        />
      )}
    </>
  );
};

export default ProductDetailClient;
